/**
 * ADA Job Enrichment Utility
 * Enriches job requirements with real company data, job postings, and contact details
 * Used for analyst reporting and candidate sourcing
 * 
 * Data stored includes:
 * - Company name and verified details
 * - Job posting source (LinkedIn, Indeed, company website, etc.)
 * - Actual job posting link
 * - Contact details (HR email, hiring manager)
 * - Salary range and benefits
 * - Required skills breakdown
 * - ADA compliance score (0-100)
 * - Engagement metrics (views, applications, qualified candidates)
 */

import { saveEnrichedJobs, loadEnrichedJobs } from './adaJobPersistence';

// Enriched jobs database
const enrichedJobs = new Map();
const jobSearchCache = new Map(); // Cache web search results to avoid duplicates
const analyticsData = new Map(); // Day-wise analytics

/**
 * Enrich a job with company and real posting data
 * Combines Gemini AI output with web search results
 * @param {Object} baseJob - Basic job from Gemini
 * @param {string} companyName - Company name to search for
 * @returns {Promise<Object>} Enriched job with complete details
 */
export const enrichJobWithWebData = async (baseJob, companyName = null) => {
  try {
    const jobTitle = baseJob.jobTitle;
    const industry = baseJob.industry;
    
    console.log('🔍 Enriching job:', jobTitle, 'for', companyName || industry);
    
    // Step 1: Search for real job postings
    const jobPostings = await searchJobPostings(jobTitle, industry, companyName);
    
    // Step 2: Extract company details from top result
    const primaryPosting = jobPostings[0] || {};
    
    // Step 3: Calculate ADA compliance score
    const adaScore = calculateAdaComplianceScore(baseJob);
    
    // Step 4: Build enriched job object
    const enrichedJob = {
      id: baseJob.id,
      jobTitle,
      industry,
      level: baseJob.level,
      
      // Enriched company data
      company: {
        name: primaryPosting.companyName || companyName || 'Not Found',
        verified: !!primaryPosting.companyName,
        website: primaryPosting.companyWebsite || null,
        hrEmail: primaryPosting.hrEmail || null,
        hiringManager: primaryPosting.hiringManager || null,
        phone: primaryPosting.phone || null,
        linkedinPage: primaryPosting.linkedinPage || null
      },
      
      // Job posting details
      posting: {
        source: primaryPosting.source || 'Unknown',
        link: primaryPosting.link || null,
        postedDate: primaryPosting.postedDate || new Date().toISOString(),
        expiryDate: primaryPosting.expiryDate || null,
        applicants: primaryPosting.applicantCount || 0,
        views: primaryPosting.views || 0
      },
      
      // Salary & Benefits
      compensation: {
        salaryMin: primaryPosting.salaryMin || null,
        salaryMax: primaryPosting.salaryMax || null,
        currency: primaryPosting.currency || 'USD',
        benefits: primaryPosting.benefits || []
      },
      
      // ADA & Accessibility
      ada: {
        complianceScore: adaScore,
        remoteFriendly: baseJob.remoteScore >= 7,
        flexibleSchedule: true,
        accommodations: baseJob.requiredAccommodations || [],
        accessibilityFeatures: parseAccessibilityFeatures(baseJob.description)
      },
      
      // Skills & Requirements
      requirements: {
        technicalSkills: primaryPosting.technicalSkills || [],
        softSkills: primaryPosting.softSkills || [],
        yearsOfExperience: primaryPosting.yearsOfExperience || null,
        certifications: primaryPosting.certifications || [],
        languages: primaryPosting.languages || ['English']
      },
      
      // Original AI-generated content
      aiGenerated: {
        description: baseJob.description,
        adaFocus: baseJob.adaComplianceFocus,
        keyResponsibilities: baseJob.keyResponsibilities,
        requirements: baseJob.requirements
      },
      
      // Analyst tracking
      analytics: {
        createdAt: new Date().toISOString(),
        createdDate: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString(),
        status: 'active',
        relevanceScore: calculateRelevanceScore(baseJob, primaryPosting),
        tier: getTierFromRelevance(calculateRelevanceScore(baseJob, primaryPosting)),
        priority: 'medium' // high, medium, low based on ADA score
      },
      
      // Candidate sourcing
      sourcing: {
        targetCandidates: [],
        candidatesApplied: 0,
        candidatesQualified: 0,
        conversionRate: 0,
        sourcingNotes: ''
      },
      
      // Engagement metrics
      engagement: {
        views: 0,
        shares: 0,
        applications: 0,
        interviews: 0
      }
    };
    
    // Store enriched job
    enrichedJobs.set(enrichedJob.id, enrichedJob);
    
    console.log('✅ Job enriched:', enrichedJob.company.name, '-', enrichedJob.jobTitle);
    logDailyAnalytics(enrichedJob);
    
    // Auto-save to GitHub persistence layer
    try {
      const allJobs = Array.from(enrichedJobs.values());
      await saveEnrichedJobs(allJobs);
    } catch (saveError) {
      console.warn('⚠️ Could not save to persistence layer:', saveError.message);
    }
    
    return enrichedJob;
  } catch (error) {
    console.error('❌ Error enriching job:', error);
    // Return base job if enrichment fails
    return {
      ...baseJob,
      company: { name: 'Data Unavailable', verified: false },
      posting: { source: 'Gemini AI Only', link: null },
      analytics: { createdAt: new Date().toISOString(), relevanceScore: 0 }
    };
  }
};

/**
 * Search for actual job postings using web search
 * Looks at LinkedIn, Indeed, company websites, job boards
 * @param {string} jobTitle - Job title to search
 * @param {string} industry - Industry for filtering
 * @param {string} companyName - Specific company (optional)
 * @returns {Promise<Array>} Array of job postings with details
 */
async function searchJobPostings(jobTitle, industry, companyName = null) {
  const cacheKey = `${jobTitle}_${industry}_${companyName || 'any'}`;
  
  // Check cache first
  if (jobSearchCache.has(cacheKey)) {
    console.log('📦 Using cached search results');
    return jobSearchCache.get(cacheKey);
  }

  try {
    console.log('🌐 Searching for real job postings...');
    
    // Build search query
    let searchQuery = `${jobTitle} jobs ${industry}`;
    if (companyName) {
      searchQuery = `${companyName} ${jobTitle} jobs`;
    }
    
    // Use web search to find job postings
    const results = await performWebSearch(searchQuery);
    
    // Parse results to extract job posting data
    const jobPostings = results.slice(0, 5).map(result => parseJobPosting(result));
    
    // Cache results for 24 hours
    jobSearchCache.set(cacheKey, jobPostings);
    
    return jobPostings;
  } catch (error) {
    console.error('Web search failed:', error);
    return [];
  }
}

/**
 * Perform web search (requires API integration - Brave Search, Google Search, etc.)
 * @param {string} query - Search query
 * @returns {Promise<Array>} Search results
 */
async function performWebSearch(query) {
  // TODO: Integrate with Brave Search API or Google Search API
  // For now, return mock data to demonstrate structure
  
  console.log('🔎 Web search for:', query);
  
  // Mock job posting data - replace with real API calls
  const mockResults = [
    {
      title: 'Software Engineer - ADA Compliant Role',
      url: 'https://linkedin.com/jobs/view/...',
      snippet: 'Looking for experienced software engineer with ADA compliance focus',
      company: 'TechCorp Inc',
      source: 'LinkedIn',
      posted: '2 days ago'
    },
    {
      title: 'Senior Developer Wanted',
      url: 'https://indeed.com/jobs/view/...',
      snippet: 'Remote position, flexible schedule, inclusive workplace',
      company: 'InnovateSoft',
      source: 'Indeed',
      posted: '1 week ago'
    }
  ];
  
  return mockResults;
}

/**
 * Parse job posting HTML/API response to extract structured data
 * @param {Object} result - Web search result
 * @returns {Object} Parsed job posting data
 */
function parseJobPosting(result) {
  return {
    companyName: result.company || extractCompanyName(result.snippet),
    source: result.source || 'Unknown',
    link: result.url || null,
    postedDate: new Date().toISOString(),
    applicantCount: Math.floor(Math.random() * 100),
    views: Math.floor(Math.random() * 500),
    
    // These would be extracted via web scraping or API
    salaryMin: 80000,
    salaryMax: 150000,
    currency: 'USD',
    benefits: ['Health Insurance', 'Remote Work', 'Flexible Schedule', '401k'],
    
    technicalSkills: ['JavaScript', 'React', 'Node.js', 'ADA Standards'],
    softSkills: ['Communication', 'Problem Solving', 'Team Collaboration'],
    yearsOfExperience: 5,
    certifications: ['WCAG 2.1 Certified'],
    
    companyWebsite: 'https://company.com',
    hrEmail: 'hr@company.com',
    hiringManager: 'John Smith',
    phone: '(555) 123-4567',
    linkedinPage: 'https://linkedin.com/company/...'
  };
}

/**
 * Extract company name from search result
 * @param {string} snippet - Search snippet
 * @returns {string} Company name
 */
function extractCompanyName(snippet) {
  // Simple extraction - could be improved with NLP
  const words = snippet.split(' ');
  return words.slice(0, 2).join(' ') || 'Unknown Company';
}

/**
 * Calculate ADA compliance score (0-100)
 * Based on accommodations, remote friendliness, accessibility features
 * @param {Object} job - Job object
 * @returns {number} Compliance score
 */
function calculateAdaComplianceScore(job) {
  let score = 50; // Base score
  
  // Add points for features
  if (job.remoteScore >= 7) score += 20;
  if (job.requiredAccommodations) score += 15;
  if (job.keyResponsibilities && job.keyResponsibilities.includes('flexible')) score += 10;
  if (job.description && job.description.toLowerCase().includes('accessibility')) score += 5;
  
  return Math.min(score, 100);
}

/**
 * Parse accessibility features from job description
 * @param {string} description - Job description
 * @returns {Array} Accessibility features
 */
function parseAccessibilityFeatures(description) {
  const features = [];
  
  if (description.toLowerCase().includes('remote')) features.push('Remote Work');
  if (description.toLowerCase().includes('flexible')) features.push('Flexible Schedule');
  if (description.toLowerCase().includes('accommodation')) features.push('Custom Accommodations');
  if (description.toLowerCase().includes('screen reader')) features.push('Screen Reader Compatible');
  if (description.toLowerCase().includes('keyboard')) features.push('Keyboard Navigation');
  if (description.toLowerCase().includes('captions')) features.push('Video Captions');
  
  return features.length > 0 ? features : ['Standard Accommodations'];
}

/**
 * Calculate relevance score for ranking (0-100)
 * @param {Object} job - AI-generated job
 * @param {Object} posting - Web posting data
 * @returns {number} Relevance score
 */
function calculateRelevanceScore(job, posting) {
  let score = 50; // Base
  
  if (posting.companyName) score += 20; // Company found
  if (posting.link) score += 15; // Has direct link
  if (posting.hrEmail) score += 10; // Contact found
  if (posting.salaryMin) score += 5; // Salary info available
  
  return Math.min(score, 100);
}

/**
 * Get tier (Top 25%, Top 50%, etc.) based on relevance
 * @param {number} score - Relevance score
 * @returns {string} Tier level
 */
function getTierFromRelevance(score) {
  if (score >= 90) return 'Tier 1 (Top 10%)';
  if (score >= 80) return 'Tier 2 (Top 25%)';
  if (score >= 70) return 'Tier 3 (Top 50%)';
  return 'Tier 4 (Standard)';
}

/**
 * Get all enriched jobs
 * @param {Object} filters - Filter options {industry, level, date, tier, adaScore}
 * @returns {Array} Filtered and sorted jobs
 */
export const getEnrichedJobs = (filters = {}) => {
  let jobs = Array.from(enrichedJobs.values());
  
  // Apply filters
  if (filters.industry) {
    jobs = jobs.filter(j => j.industry.toLowerCase().includes(filters.industry.toLowerCase()));
  }
  
  if (filters.level) {
    jobs = jobs.filter(j => j.level === filters.level);
  }
  
  if (filters.date) {
    jobs = jobs.filter(j => j.analytics.createdDate === filters.date);
  }
  
  if (filters.tier) {
    jobs = jobs.filter(j => j.analytics.tier === filters.tier);
  }
  
  if (filters.adaScoreMin) {
    jobs = jobs.filter(j => j.ada.complianceScore >= filters.adaScoreMin);
  }
  
  // Sort by relevance score descending
  return jobs.sort((a, b) => 
    (b.analytics.relevanceScore || 0) - (a.analytics.relevanceScore || 0)
  );
};

/**
 * Get top 25% of jobs for analyst report
 * @param {string} date - Specific date (YYYY-MM-DD) or 'today'
 * @returns {Array} Top 25% jobs with complete details
 */
export const getTop25PercentJobs = (date = 'today') => {
  const targetDate = date === 'today' ? new Date().toISOString().split('T')[0] : date;
  
  const allJobs = getEnrichedJobs({ date: targetDate });
  const top25Count = Math.ceil(allJobs.length * 0.25) || 1;
  
  return allJobs.slice(0, top25Count).map((job, index) => ({
    ...job,
    rank: index + 1,
    tier: 'Top 25%'
  }));
};

/**
 * Get day-wise analytics for analyst dashboard
 * @param {string} date - Specific date or 'today'
 * @returns {Object} Daily metrics and insights
 */
export const getDayWiseAnalytics = (date = 'today') => {
  const targetDate = date === 'today' ? new Date().toISOString().split('T')[0] : date;
  
  const dayJobs = getEnrichedJobs({ date: targetDate });
  
  return {
    date: targetDate,
    totalJobs: dayJobs.length,
    top25Count: Math.ceil(dayJobs.length * 0.25) || 0,
    averageAdaScore: dayJobs.reduce((sum, j) => sum + (j.ada.complianceScore || 0), 0) / dayJobs.length || 0,
    averageRelevance: dayJobs.reduce((sum, j) => sum + (j.analytics.relevanceScore || 0), 0) / dayJobs.length || 0,
    
    byIndustry: groupBy(dayJobs, 'industry'),
    byLevel: groupBy(dayJobs, 'level'),
    bySource: groupBy(dayJobs, j => j.posting.source),
    
    topCompanies: dayJobs
      .filter(j => j.company.verified)
      .slice(0, 10)
      .map(j => ({
        name: j.company.name,
        jobsPosted: 1,
        contact: j.company.hrEmail
      })),
    
    totalApplications: dayJobs.reduce((sum, j) => sum + j.engagement.applications, 0),
    totalQualifiedCandidates: dayJobs.reduce((sum, j) => sum + j.sourcing.candidatesQualified, 0)
  };
};

/**
 * Helper: Group array by key
 */
function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const keyVal = typeof key === 'function' ? key(item) : item[key];
    acc[keyVal] = (acc[keyVal] || 0) + 1;
    return acc;
  }, {});
}

/**
 * Log daily analytics
 */
function logDailyAnalytics(job) {
  const date = job.analytics.createdDate;
  if (!analyticsData.has(date)) {
    analyticsData.set(date, {
      jobsCreated: 0,
      totalAdaScore: 0,
      companiesFound: 0
    });
  }
  
  const dailyStats = analyticsData.get(date);
  dailyStats.jobsCreated++;
  dailyStats.totalAdaScore += job.ada.complianceScore;
  if (job.company.verified) dailyStats.companiesFound++;
}

/**
 * Generate CSV report for top 25% jobs
 * @param {string} date - Date for report
 * @returns {string} CSV formatted data
 */
export const generateAnalystReport = (date = 'today') => {
  const jobs = getTop25PercentJobs(date);
  
  if (jobs.length === 0) return 'No jobs found for the specified date';
  
  const headers = [
    'Rank', 'Job Title', 'Company', 'Industry', 'Level',
    'ADA Score', 'Relevance', 'Salary Min', 'Salary Max',
    'Source', 'Posted Date', 'HR Email', 'Contact', 'Link'
  ];
  
  const rows = jobs.map(job => [
    job.rank,
    job.jobTitle,
    job.company.name,
    job.industry,
    job.level,
    job.ada.complianceScore,
    job.analytics.relevanceScore,
    job.compensation.salaryMin || 'N/A',
    job.compensation.salaryMax || 'N/A',
    job.posting.source,
    job.posting.postedDate,
    job.company.hrEmail || 'N/A',
    job.company.phone || 'N/A',
    job.posting.link || 'N/A'
  ]);
  
  const csv = [
    headers.join(','),
    ...rows.map(r => r.map(v => `"${v}"`).join(','))
  ].join('\n');
  
  return csv;
};

/**
 * Update candidate sourcing data for a job
 * @param {string} jobId - Job ID
 * @param {Object} data - Sourcing data {targetCandidates, applied, qualified}
 */
export const updateJobSourcingData = (jobId, data) => {
  const job = enrichedJobs.get(jobId);
  if (job) {
    job.sourcing = {
      ...job.sourcing,
      ...data,
      conversionRate: data.qualified ? (data.qualified / data.applied * 100).toFixed(2) : 0
    };
    job.analytics.lastUpdated = new Date().toISOString();
  }
  return job;
};

/**
 * Load enriched jobs from GitHub persistence layer
 * Call on component mount to restore previous session data
 * @returns {Promise<Array>} Loaded enriched jobs
 */
export const loadPersistedJobs = async () => {
  try {
    const persistedJobs = await loadEnrichedJobs();
    
    // Restore to in-memory map
    persistedJobs.forEach(job => {
      // Normalize date formats for createdDate to YYYY-MM-DD
      try {
        const cd = job?.analytics?.createdDate;
        if (cd && !/^\d{4}-\d{2}-\d{2}$/.test(cd)) {
          // Attempt to parse common formats (D/M/YYYY, DD/MM/YYYY, D-M-YYYY, etc.)
          let parsed = null;
          // D/M/YYYY or DD/MM/YYYY
          if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cd)) {
            const [d, m, y] = cd.split('/');
            parsed = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(cd)) {
            const [d, m, y] = cd.split('-');
            parsed = `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
          } else {
            // Last resort: try Date parse
            const dt = new Date(cd);
            if (!isNaN(dt.getTime())) parsed = dt.toISOString().split('T')[0];
          }

          if (parsed) {
            job.analytics = job.analytics || {};
            job.analytics.createdDate = parsed;
          }
        }
      } catch (e) {
        // ignore parsing errors
      }

      enrichedJobs.set(job.id, job);
    });
    
    console.log(`📂 Loaded ${persistedJobs.length} persisted enriched jobs`);
    return persistedJobs;
  } catch (error) {
    console.error('❌ Error loading persisted jobs:', error);
    return [];
  }
};

/**
 * Get single job by id from in-memory store
 * @param {string} id
 * @returns {Object|null}
 */
export const getJobById = (id) => {
  return enrichedJobs.get(id) || null;
};

export default {
  enrichJobWithWebData,
  getEnrichedJobs,
  getTop25PercentJobs,
  getDayWiseAnalytics,
  generateAnalystReport,
  updateJobSourcingData,
  loadPersistedJobs
};

